import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { useApi } from "./useApi";
import type { BankAccountResponse } from "@models/bankAccount";
import { asApiFailure } from "@utils/apiFailure";
import { isFetchingBankAccountsAtom } from "@/store/bankAccountAtoms";

const ORGANIZATIONS_ENDPOINT = "/organizations";

export const useBankAccountApi = () => {
    const { callApi } = useApi();
    const setIsFetching = useSetAtom(isFetchingBankAccountsAtom);

    // The org's single active saved ACH profile for the checkout picker (#296).
    // CONFIRMED — Team B PR 242 @ 1a05d33 (see models/bankAccount.ts). The response
    // is masked metadata only — never routing or account numbers.
    //
    // 404 = no active bank profile: a NORMAL state, resolved as null. Every failure
    // keeps the toast suppressed — the caller (PaymentForm) renders its own inline
    // degradation, so an environment deployed before PR 242 must not spray error
    // toasts over a working ACH form.
    const getActiveBankAccount = useCallback(
        async (
            organizationId: string,
        ): Promise<BankAccountResponse | null> => {
            setIsFetching(true);

            try {
                return await callApi<BankAccountResponse>(
                    `${ORGANIZATIONS_ENDPOINT}/${organizationId}/bank-account`,
                    { skipErrorNotification: true },
                );
            } catch (error) {
                // The shared duck-type (@utils/apiFailure) rather than a local copy:
                // instanceof would couple tests to the real useApi module, which ts-jest
                // can't load, and a hand-rolled check here diverged from asApiFailure on a
                // plain `{ status: 404 }` rejection.
                if (asApiFailure(error)?.status === 404) {
                    return null;
                }
                throw error;
            } finally {
                setIsFetching(false);
            }
        },
        [callApi, setIsFetching],
    );

    return {
        getActiveBankAccount,
    };
};
